import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import en from './locales/en.json'
import nl from './locales/nl.json'
import de from './locales/de.json'
import fr from './locales/fr.json'
import es from './locales/es.json'
import it from './locales/it.json'

// Supported locales
export const SUPPORTED_LOCALES = [
  { code: 'en-US', name: 'English', flag: '🇺🇸' },
  { code: 'nl-NL', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'de-DE', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr-FR', name: 'Français', flag: '🇫🇷' },
  { code: 'es-ES', name: 'Español', flag: '🇪🇸' },
  { code: 'it-IT', name: 'Italiano', flag: '🇮🇹' },
] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]['code']

// Default locale
export const DEFAULT_LOCALE: SupportedLocale = 'en-US'

// Storage key for persisting locale preference
const LOCALE_STORAGE_KEY = 'bonnie-locale'

// Get initial locale from localStorage or default
function getInitialLocale(): string {
  if (typeof window === 'undefined') return DEFAULT_LOCALE

  // Try localStorage first
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) {
    return stored
  }

  // Try browser language
  const browserLang = navigator.language
  if (SUPPORTED_LOCALES.some((l) => l.code === browserLang)) {
    return browserLang
  }

  // Check if browser language starts with a supported language code
  const langPrefix = browserLang.split('-')[0]
  const match = SUPPORTED_LOCALES.find((l) => l.code.startsWith(langPrefix))
  if (match) {
    return match.code
  }

  return DEFAULT_LOCALE
}

// Initialize i18next
i18n.use(initReactI18next).init({
  resources: {
    'en-US': { translation: en },
    'nl-NL': { translation: nl },
    'de-DE': { translation: de },
    'fr-FR': { translation: fr },
    'es-ES': { translation: es },
    'it-IT': { translation: it },
  },
  lng: getInitialLocale(),
  fallbackLng: {
    'nl-NL': ['nl', 'en-US'],
    nl: ['nl-NL', 'en-US'],
    'de-DE': ['de', 'en-US'],
    de: ['de-DE', 'en-US'],
    'fr-FR': ['fr', 'en-US'],
    fr: ['fr-FR', 'en-US'],
    'es-ES': ['es', 'en-US'],
    es: ['es-ES', 'en-US'],
    'it-IT': ['it', 'en-US'],
    it: ['it-IT', 'en-US'],
    default: ['en-US'],
  },
  interpolation: {
    escapeValue: false, // React already escapes
  },
  react: {
    useSuspense: false, // Avoid suspense issues during SSR/initial render
  },
})

// Keep <html lang> in sync so browser translators see the real page language
// instead of the hardcoded lang="en" from index.html.
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng
  })
}

// Helper to persist locale choice
export function setLocale(locale: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  }
  i18n.changeLanguage(locale)
}

// Helper to get current locale
export function getLocale(): string {
  return i18n.language || DEFAULT_LOCALE
}

// Normalize locale codes (e.g., en_US -> en-US)
export function normalizeLocale(locale: string): string {
  return locale.replace('_', '-')
}

export default i18n
